<template>
  <section class="info-section">
    <div class="section-header">
      <h2>🎯 {{ $t('info.scenariosTitle') }}</h2>
      <div class="header-decoration">
        <div class="decoration-item">✨</div>
        <div class="decoration-item">🚀</div>
        <div class="decoration-item">🎮</div>
      </div>
    </div>
    
    <div class="scenarios-grid">
      <div
        v-for="(scenario, index) in scenarios"
        :key="index"
        class="scenario-card gamified-card"
        :class="{'premium-feature': scenario.premium}"
        @mouseenter="hoverCard(index)"
        @mouseleave="resetCard"
      >
        <div class="card-icon">{{ scenario.icon }}</div>
        <div class="card-content">
          <h3 class="card-title">{{ scenario.title }}</h3>
          <p class="card-description">{{ scenario.description }}</p>
        </div>
        <div v-if="scenario.premium" class="premium-badge">
          <span>💎 {{ $t('info.premium') }}</span>
        </div>
        <div class="card-hover-effect"></div>
      </div>
    </div>
    
    <div class="section-footer">
      <p class="disclaimer">{{ $t('info.disclaimer') }}</p>
    </div>
  </section>
</template>

<script>
import { reactive } from 'vue'

export default {
  name: 'InfoSection',
  setup() {
    const scenarios = reactive([
      {
        icon: '🎬',
        title: 'Video Dubbing – YouTube & TikTok Voice Generator',
        description: 'TTSMaker, as a versatile AI voice generator, can generate AI voices for various scenarios and is widely used in video dubbing for platforms like YouTube and TikTok.',
        premium: false
      },
      {
        icon: '📚',
        title: 'Audiobook Reading – Create and Listen to Audiobooks',
        description: 'TTSMaker can convert text into natural speech, allowing you to easily create and enjoy audiobooks, bringing stories to life through immersive narration.',
        premium: true
      },
      {
        icon: '🎓',
        title: 'Education & Training – Teaching and Learning Languages',
        description: 'TTSMaker can convert text to sound and read it aloud, helping you learn the pronunciation of words. It supports multiple languages and has become a useful tool for language learners.',
        premium: false
      },
      {
        icon: '📢',
        title: 'Marketing & Advertising – Create Voiceovers for Video Ads',
        description: 'TTSMaker generates persuasive voice-overs to help marketers and advertisers explain a product\'s features with high-quality audio.',
        premium: true
      }
    ])

    const hoverCard = (index) => {
      // Efecto de hover podría añadirse aquí
      console.log('Hovering card:', index)
    }

    const resetCard = () => {
      // Resetear efectos de hover
    }

    return {
      scenarios,
      hoverCard,
      resetCard
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.info-section {
  padding: 40px 20px;
  background: $dark-bg-primary;
  margin: 40px 0;
  position: relative;
  overflow: hidden;
  
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  
  h2 {
    font-size: 2.2rem;
    font-weight: 800;
    color: $dark-accent-primary;
    margin-bottom: 15px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
}

.header-decoration {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;
  
  .decoration-item {
    font-size: 1.8rem;
    animation: float 3s ease-in-out infinite;
    
    &:nth-child(2) {
      animation-delay: 0.5s;
    }
    
    &:nth-child(3) {
      animation-delay: 1s;
    }
  }
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  max-width: 1200px;
  margin: 0 auto;
}

.scenario-card {
  position: relative;
  padding: 25px;
  border-radius: 16px;
  background: $dark-bg-secondary;
  border: 1px solid $dark-border-color;
  box-shadow: $dark-card-shadow;
  transition: all 0.3s ease;
  overflow: hidden;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: $dark-accent-primary;
    
    .card-hover-effect {
      opacity: 1;
    }
  }
  
  &.premium-feature {
    border: 2px solid rgba(187, 134, 252, 0.3);
    background: linear-gradient(135deg, rgba(187, 134, 252, 0.1) 0%, rgba(3, 218, 198, 0.1) 100%);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 0 40px 40px 0;
      border-color: transparent $dark-accent-primary transparent transparent;
    }
  }
}

.card-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  text-align: center;
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: $dark-text-primary;
  margin-bottom: 15px;
  line-height: 1.4;
}

.card-description {
  color: $dark-text-secondary;
  line-height: 1.6;
  flex: 1;
}

.premium-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, $dark-accent-primary, $accent-color);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  color: white;
}

.card-hover-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, $dark-accent-primary, $dark-accent-secondary, $dark-accent-tertiary);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.section-footer {
  text-align: center;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid $dark-border-color;
}

.disclaimer {
  color: $dark-text-secondary;
  font-size: 0.9rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

// Responsive design
@media (max-width: 768px) {
  .info-section {
    padding: 30px 15px;
    margin: 30px 0;
  }
  
  .section-header h2 {
    font-size: 1.8rem;
  }
  
  .scenarios-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .scenario-card {
    min-height: auto;
    padding: 20px;
  }
  
  .card-icon {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }
  
  .card-title {
    font-size: 1.2rem;
  }
}
</style>